import {
  AddressSet,
  hexEq,
  json_stringify,
  type MarketData,
  type MarketStateHuman,
  type MarketSuite,
  type OnchainSDK,
  type TokenMetaData,
  type TokensMetaState,
} from "@gearbox-protocol/sdk/onchain";
import type { Address } from "viem";
import { stringify } from "yaml";

export type SdkStateLanguage = "yaml" | "json";

export interface SdkStateFilter {
  curator?: Address;
  market?: Address;
  creditManager?: Address;
}

export interface SerializeSdkStateArgs {
  sdk: OnchainSDK;
  language: SdkStateLanguage;
  filter: SdkStateFilter;
}

interface SelectMarketsArgs {
  sdk: OnchainSDK;
  filter: SdkStateFilter;
}

interface NarrowCreditManagersArgs {
  market: MarketSuite;
  creditManager: Address | undefined;
}

interface MarketTokenAddressesArgs {
  sdk: OnchainSDK;
  market: MarketSuite;
}

interface SuiteTokenAddressesArgs {
  market: MarketSuite;
  creditManager: Address;
}

interface SelectTokenAddressesArgs {
  sdk: OnchainSDK;
  markets: MarketSuite[];
  creditManager: Address | undefined;
}

interface FilterTokensArgs {
  sdk: OnchainSDK;
  addresses: Address[];
}

interface FilteredTokens {
  metas: TokenMetaData[];
  state: TokensMetaState | undefined;
}

function selectMarkets(args: SelectMarketsArgs): MarketSuite[] {
  const { sdk, filter } = args;
  return sdk.marketRegister.markets.filter(market => {
    if (filter.curator && !hexEq(market.configurator.address, filter.curator)) {
      return false;
    }
    if (filter.market && !hexEq(market.pool.pool.address, filter.market)) {
      return false;
    }
    return true;
  });
}

function marketHuman(args: NarrowCreditManagersArgs): MarketStateHuman {
  const snapshot = args.market.stateHuman();
  const { creditManager } = args;
  if (!creditManager) {
    return snapshot;
  }
  const suite = args.market.creditManagers.find(cm =>
    hexEq(cm.creditManager.address, creditManager),
  );
  return {
    ...snapshot,
    creditManagers: suite ? [suite.stateHuman()] : [],
  };
}

function marketRaw(args: NarrowCreditManagersArgs): MarketData {
  const { creditManager } = args;
  if (!creditManager) {
    return args.market.state;
  }
  const suite = args.market.creditManagers.find(cm =>
    hexEq(cm.creditManager.address, creditManager),
  );
  return {
    ...args.market.state,
    creditManagers: suite ? [suite.state] : [],
  };
}

/** Compressor tokens, pool share, zapper legs, and RWA factory DS tokens. */
function marketTokenAddresses(args: MarketTokenAddressesArgs): Address[] {
  const { sdk, market } = args;
  const pool = market.pool.pool.address;
  const zapperTokens =
    sdk.marketRegister.state.zappers
      ?.filter(zapper => hexEq(zapper.pool, pool))
      .flatMap(zapper => [zapper.tokenIn.addr, zapper.tokenOut.addr]) ?? [];
  return [
    ...market.state.tokens.map(token => token.addr),
    pool,
    ...zapperTokens,
    ...(market.rwaFactory?.getTokens() ?? []),
  ];
}

/**
 * Collateral, underlying, and unwrapped underlying.
 * Empty when this market has no matching credit suite.
 */
function suiteTokenAddresses(args: SuiteTokenAddressesArgs): Address[] {
  const suite = args.market.creditManagers.find(cm =>
    hexEq(cm.creditManager.address, args.creditManager),
  );
  if (!suite) {
    return [];
  }
  return [
    ...suite.creditManager.collateralTokens,
    suite.underlying,
    args.market.unwrappedUnderlying,
  ];
}

/**
 * Suite addresses when `creditManager` is set, otherwise the union of
 * `marketTokenAddresses` over `markets`.
 */
function selectTokenAddresses(args: SelectTokenAddressesArgs): Address[] {
  const { sdk, markets, creditManager } = args;
  if (creditManager) {
    return markets.flatMap(market =>
      suiteTokenAddresses({ market, creditManager }),
    );
  }
  return markets.flatMap(market => marketTokenAddresses({ sdk, market }));
}

/**
 * Registry entries whose `addr` is in `addresses`, in `tokensMeta.values()` order.
 * `state` is omitted when `sdk.state.tokens` is absent.
 */
function filterTokens(args: FilterTokensArgs): FilteredTokens {
  const wanted = new AddressSet(args.addresses);
  const metas = args.sdk.tokensMeta
    .values()
    .filter(token => wanted.has(token.addr));
  const raw = args.sdk.state.tokens;
  if (!raw) {
    return { metas, state: undefined };
  }
  return {
    metas,
    state: {
      tokens: metas,
      extendedLoaded: raw.extendedLoaded.filter(address => wanted.has(address)),
    },
  };
}

/**
 * Serializes SDK state as YAML or JSON.
 * Curator and market filters narrow `markets` and `tokens`.
 * A credit suite narrows both further, and only when a market is selected.
 * Tokens stay intact when no curator or market is selected.
 */
export function serializeSdkState(args: SerializeSdkStateArgs): string {
  const markets = selectMarkets(args);
  const creditManager = args.filter.market
    ? args.filter.creditManager
    : undefined;
  const tokensActive = Boolean(args.filter.curator || args.filter.market);
  const tokens = tokensActive
    ? filterTokens({
        sdk: args.sdk,
        addresses: selectTokenAddresses({
          sdk: args.sdk,
          markets,
          creditManager,
        }),
      })
    : undefined;

  if (args.language === "yaml") {
    return stringify(
      {
        ...args.sdk.stateHuman(),
        ...(tokens ? { tokens: tokens.metas } : {}),
        markets: markets.map(market => marketHuman({ market, creditManager })),
      },
      { lineWidth: 0 },
    );
  }

  return json_stringify({
    ...args.sdk.state,
    ...(tokens ? { tokens: tokens.state } : {}),
    markets: markets.map(market => marketRaw({ market, creditManager })),
  });
}
